import type { WebChatConfig } from "@chatbotgang/web-sdk-core/model";
import { WebChatLauncherPositionEnum } from "@chatbotgang/web-sdk-core/model";
import { css } from "@emotion/react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { AvatarImageUpload } from "@/components/ImageUpload/AvatarImageUpload";
import { NumberInput } from "@/components/Input/NumberInput";
import { Typography } from "@/components/Typography";
import type { CssRecord } from "@/internal/emotion";
import { useWebChatConfigForm } from "@/pages/channels/hooks/form/useWebChatConfigForm";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { useUploadImageMutation } from "@/resources/attachment/useUploadImageMutation";

import { ModeEnum } from ".";

const styles = {
  sectionTitle: css({
    fontWeight: 500,
  }),
  subsectionTitle: css({
    fontWeight: 500,
  }),
  positionDescription: css({
    color: theme.colors.neutral050,
  }),
} satisfies CssRecord;

export const LauncherForm: React.FC = function LauncherForm() {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<WebChatConfig>();
  const { channel } = ChannelQueriesContext.useData();
  const widgetImageInputRef = useRef<HTMLInputElement>(null);
  const dialogueId = useMemo(
    () =>
      channel.type === "wccs" && channel.externalChannelId
        ? channel.externalChannelId
        : "",
    [channel.type, channel.externalChannelId],
  );
  const widgetImageUploadMutation = useUploadImageMutation({
    onSuccess: (url: string) => {
      setValue("launcher.widgetImageUrl", url, { shouldDirty: true });
    },
    onSettled: () => {
      if (widgetImageInputRef.current) {
        widgetImageInputRef.current.value = "";
      }
    },
  });

  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="h3" css={styles.sectionTitle}>
          {t("webChatConfig.launcher.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap="16px">
          <Stack direction="column" gap="8px">
            <Typography variant="h3" css={styles.subsectionTitle}>
              {t("webChatConfig.launcher.widgetImage")}
            </Typography>
            <Controller
              name="launcher.widgetImageUrl"
              control={control}
              render={({ field }) => (
                <AvatarImageUpload
                  inputRef={widgetImageInputRef}
                  value={field.value}
                  onChange={(file) => {
                    if (!dialogueId) return;
                    widgetImageUploadMutation.mutate({
                      feature: "launcherWidget",
                      dialogueId,
                      file,
                    });
                  }}
                  onClear={() => {
                    setValue(
                      "launcher.widgetImageUrl",
                      useWebChatConfigForm.defaultWebChatConfig.launcher
                        .widgetImageUrl,
                      { shouldDirty: true },
                    );
                  }}
                  disable={widgetImageUploadMutation.isPending}
                />
              )}
            />
          </Stack>
          <Stack direction="column" gap="8px">
            <Typography variant="h3" css={styles.subsectionTitle}>
              {t("webChatConfig.launcher.position")}
            </Typography>
            <Typography variant="note">
              {t("webChatConfig.launcher.positionDescription")}
            </Typography>
          </Stack>
          {ModeEnum.options.map((settingType) => (
            <Stack direction="column" gap="8px" key={settingType}>
              <Typography variant="body" css={styles.subsectionTitle}>
                {t(`webChatConfig.launcher.${settingType}`)}
              </Typography>
              <Controller
                name={`launcher.${settingType}Setting.buttonPosition`}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <FormControlLabel
                      value={WebChatLauncherPositionEnum.enum.bottomLeft}
                      control={<Radio />}
                      label={
                        <Typography>
                          {t("webChatConfig.launcher.bottomLeft")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value={WebChatLauncherPositionEnum.enum.bottomRight}
                      control={<Radio />}
                      label={
                        <Typography>
                          {t("webChatConfig.launcher.bottomRight")}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                )}
              />
              <Stack direction="row" gap="8px">
                <Stack gap="8px">
                  <Typography variant="body">
                    {t("webChatConfig.launcher.bottomSpacing")}
                  </Typography>
                  <Controller
                    name={`launcher.${settingType}Setting.bottomSpacingPx`}
                    control={control}
                    render={({ field }) => (
                      <Stack direction="row" gap="8px" alignItems="center">
                        <Trans
                          i18nKey="webChatConfig.launcher.px"
                          components={{
                            TextField: (
                              <NumberInput
                                key="textfield"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                ref={field.ref}
                                fullWidth
                              />
                            ),
                            Typography: (
                              <Typography key="typography" variant="body" />
                            ),
                          }}
                        />
                      </Stack>
                    )}
                  />
                </Stack>
                <Stack gap="8px">
                  <Typography variant="body">
                    {t("webChatConfig.launcher.sideSpacing")}
                  </Typography>
                  <Controller
                    name={`launcher.${settingType}Setting.inlineSpacingPx`}
                    control={control}
                    render={({ field }) => (
                      <Stack direction="row" gap="8px" alignItems="center">
                        <Trans
                          i18nKey="webChatConfig.launcher.px"
                          components={{
                            TextField: (
                              <NumberInput
                                key="textfield"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                ref={field.ref}
                                fullWidth
                              />
                            ),
                            Typography: (
                              <Typography key="typography" variant="body" />
                            ),
                          }}
                        />
                      </Stack>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
